import { useState, useCallback } from 'react';
import { getCartCode, setCartCode, removeCartCode } from '../../../utils/cartStorage';
import { captureUtmTracking, type UtmTracking } from '../../../services/tracking/events/utils/tracking.utils';

// ===== TIPOS =====

export interface CartItem {
  productId: number;
  quantity: number;
  price: number;
  name: string;
  image?: string;
  totalPrice?: number; // ✅ Adiciona totalPrice para compatibilidade
}

export interface CustomerAddress {
  postcode: string;
  street: string;
  streetNumber?: string;
  streetComplement?: string;
  streetDistrict: string;
  city: string;
  state: string;
}

export interface CustomerData {
  name: string;
  email: string;
  phone: string;
  document?: string;
  birth?: string;
  address: CustomerAddress;
}

export interface AppliedCoupon {
  code: string;
  discount: number;
  type: string;
}

export interface Cart {
  id: number;
  cartCode: string;
  status: 'active' | 'abandoned' | 'concluded';
  expiresAt: Date;
  customerData: CustomerData;
  items: CartItem[];
  totalPrice: number;
  totalDiscounts: number;
  appliedCoupons?: AppliedCoupon[];
  createdAt: Date;
  updatedAt: Date;
  abandonedAt?: Date;
  concludedAt?: Date;
  offerNanoId?: string; // ✅ Adiciona offerNanoId para validação de oferta
  currentStep?: string; // ✅ Adiciona currentStep para controle de etapas
  acceptedOrderBumps?: string[];
  tracking?: UtmTracking;
}

export interface CreateCartData {
  offerId?: number;
  storeId: string;
  customerData: CustomerData;
  items: CartItem[];
  totalPrice: number;
  totalDiscounts?: number;
  appliedCoupons?: AppliedCoupon[];
  currentStep?: string; // ✅ Adiciona currentStep no CreateCartData
  acceptedOrderBumps?: string[];
  tracking?: UtmTracking;
}

export interface UpdateCartData {
  status?: 'active' | 'abandoned' | 'concluded';
  customerData?: CustomerData;
  items?: CartItem[];
  totalPrice?: number;
  totalDiscounts?: number;
  appliedCoupons?: AppliedCoupon[];
  currentStep?: string;
  acceptedOrderBumps?: string[];
  tracking?: UtmTracking;
}

// ===== CONSTANTES =====

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'https://backend.guilhermesales.com/api';

// ===== HOOK =====

export const useCart = () => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ===== FUNÇÕES AUXILIARES (localStorage + cookie para subdomínios) =====

  const getStoredCartCode = (): string | null => getCartCode();
  const setStoredCartCode = (cartCode: string): void => setCartCode(cartCode);
  const removeStoredCartCode = (): void => removeCartCode();

  // ===== FUNÇÕES DE API =====

  const createCart = useCallback(async (data: CreateCartData): Promise<Cart> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/carts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...data, tracking: data.tracking ?? captureUtmTracking() }),
      });

      if (!response.ok) {
        throw new Error(`Erro ao criar carrinho: ${response.statusText}`);
      }

      const newCart = await response.json();
      setCart(newCart);
      setStoredCartCode(newCart.cartCode);
      return newCart;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getCart = useCallback(async (cartCode: string): Promise<Cart> => {
    setLoading(true);
    setError(null);

    try {
      const url = `${API_BASE_URL}/carts/${cartCode}`;

      const response = await fetch(url);

      if (!response.ok) {
        if (response.status === 404) {
          // Carrinho não encontrado, remove do localStorage
          removeStoredCartCode();
          setCart(null);
          throw new Error('Carrinho não encontrado');
        }
        throw new Error(`Erro ao buscar carrinho: ${response.statusText}`);
      }

      const cartData = await response.json();
      setCart(cartData);
      return cartData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCart = useCallback(async (cartCode: string, data: UpdateCartData): Promise<Cart> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/carts/${cartCode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...data, tracking: data.tracking ?? captureUtmTracking() }),
      });

      if (!response.ok) {
        throw new Error(`Erro ao atualizar carrinho: ${response.statusText}`);
      }

      const updatedCart = await response.json();
      setCart(updatedCart);
      return updatedCart;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const markCartAsAbandoned = useCallback(async (cartCode: string): Promise<Cart> => {
    try {
      const response = await fetch(`${API_BASE_URL}/carts/${cartCode}/abandoned`, {
        method: 'PATCH',
      });

      if (!response.ok) {
        throw new Error(`Erro ao marcar carrinho como abandonado: ${response.statusText}`);
      }

      const abandonedCart = await response.json();
      setCart(abandonedCart);
      return abandonedCart;
    } catch (err) {
      console.error('Erro ao marcar carrinho como abandonado:', err);
      throw err;
    }
  }, []);

  const markCartAsConcluded = useCallback(async (cartCode: string): Promise<Cart> => {
    try {
      const response = await fetch(`${API_BASE_URL}/carts/${cartCode}/concluded`, {
        method: 'PATCH',
      });

      if (!response.ok) {
        throw new Error(`Erro ao marcar carrinho como concluído: ${response.statusText}`);
      }

      const concludedCart = await response.json();
      setCart(concludedCart);
      removeStoredCartCode(); // Remove do localStorage quando concluído
      return concludedCart;
    } catch (err) {
      console.error('Erro ao marcar carrinho como concluído:', err);
      throw err;
    }
  }, []);

  // ===== FUNÇÕES PRINCIPAIS =====

  const loadExistingCart = useCallback(
    async (offerNanoId?: string): Promise<Cart | null> => {
      const storedCartCode = getStoredCartCode();

      if (!storedCartCode) {
        return null;
      }

      try {
        const cartData = await getCart(storedCartCode);

        // ✅ Validação de oferta: se offerNanoId foi fornecido, verifica se é da mesma oferta
        if (offerNanoId && cartData.offerNanoId && cartData.offerNanoId !== offerNanoId) {
          removeStoredCartCode();
          return null;
        }

        return cartData;
      } catch (err) {
        // Se não conseguir carregar, remove do localStorage
        removeStoredCartCode();
        return null;
      }
    },
    [getCart, removeStoredCartCode],
  );

  const createOrUpdateCart = useCallback(
    async (data: CreateCartData): Promise<{ cart: Cart; action: 'created' | 'updated' }> => {
      // 1. Pega cartCode (localStorage ou cookie para subdomínios)
      const cartCode = getCartCode();

      if (cartCode) {
        try {
          // 2. Tenta buscar cart existente
          const existingCart = await getCart(cartCode);

          // 3. Se cart existe E (status === 'active' OU 'abandoned') → atualiza
          if (existingCart && (existingCart.status === 'active' || existingCart.status === 'abandoned')) {
            const updatedCart = await updateCart(cartCode, {
              customerData: data.customerData,
              items: data.items,
              totalPrice: data.totalPrice,
              totalDiscounts: data.totalDiscounts,
              appliedCoupons: data.appliedCoupons,
              // ✅ Adiciona o currentStep na atualização
              currentStep: data.currentStep,
              tracking: data.tracking ?? captureUtmTracking(),
              // ✅ NÃO reativa o cart - mantém status original
            });
            return { cart: updatedCart, action: 'updated' };
          }
        } catch (error) {
          // Cart não existe ou está inativo
        }
      }

      // 4. Se não tem cartCode OU cart não existe OU status !== 'active'/'abandoned' → cria novo
      const newCart = await createCart(data);
      return { cart: newCart, action: 'created' };
    },
    [getCart, createCart, updateCart],
  );

  const abandonCart = useCallback(async (): Promise<void> => {
    const storedCartCode = getStoredCartCode();
    if (storedCartCode && cart?.status === 'active') {
      try {
        await markCartAsAbandoned(storedCartCode);
      } catch (err) {
        console.error('Erro ao abandonar carrinho:', err);
      }
    }
  }, [cart?.status, markCartAsAbandoned]);

  const concludeCart = useCallback(async (): Promise<void> => {
    const storedCartCode = getStoredCartCode();

    if (!storedCartCode) {
      return;
    }

    try {
      await markCartAsConcluded(storedCartCode);
    } catch (err) {
      console.error('Erro ao concluir carrinho:', err);
    }
  }, [markCartAsConcluded]);

  return {
    cart,
    loading,
    error,
    createOrUpdateCart,
    getCart,
    updateCart,
    abandonCart,
    concludeCart,
    loadExistingCart,
  };
};
